import { useMemo } from 'react'
import { useFormikContext } from 'formik'

import {
  serviceProductDisplayName as displayName,
  aggregateServicesByLocationId as getServices,
} from '@user/store/serviceProducts'

import { useActions } from './useActions'
import type { CheckboxOption } from '../../typings'
import type { LocAndSvcsFormModel } from '../../formConfig'
import { useSchedulingWizard } from '../../../wizardContext'
import type { ServiceCheckboxesProps } from '../ServiceCheckboxes'

export function useServiceCheckboxes(props: ServiceCheckboxesProps) {
  const { services } = props

  const {
    values: { serviceLocationId },
    errors: { serviceIds: errors },
    touched: { serviceIds: touched },
  } = useFormikContext<LocAndSvcsFormModel>()

  const [{ mode }] = useSchedulingWizard()

  const onChange = useActions()

  const { allScheduled, serviceOptions } = useMemo(() => {
    const locationServices = getServices(serviceLocationId, services)

    const serviceOptions = locationServices.map<CheckboxOption>((s) => {
      const scheduled =
        mode === 'service'
          ? s.appointmentEntityId !== null
          : s.estimateEntityId !== null

      const recurring = s.annualOccurrences > 0

      return {
        id: s.id,
        scheduled,
        recurring,
        label: displayName(s),
      }
    })

    const allScheduled =
      serviceOptions.length === 0
        ? false
        : !serviceOptions.some((x) => !x.scheduled)

    return {
      allScheduled,
      serviceOptions,
    }
  }, [mode, serviceLocationId, services])

  const showInfo = serviceLocationId === ''
  const showError = serviceLocationId !== '' && touched && errors
  const noServices = serviceLocationId !== '' && services.length === 0

  const errorStr = errors ? `${errors}` : ''

  return {
    mode,
    errorStr,
    onChange,
    showInfo,
    showError,
    noServices,
    allScheduled,
    serviceOptions,
  }
}
